/**
 * Launch Darkly flags
 * @example Usage:
 * import { LD } from 'src/app/constants/launch-darkly-flags';
 * import { useLDFlag } from 'src/app/hooks/use-ld-flag';
 *
 * const thingRollout = useLDFlag(LD.THING_ROLLOUT, false);
 * or
 * const thingRollout = useLDFlag<boolean>(LD.THING_ROLLOUT);
 *
 * @function useLDFlag - src/app/hooks/use-ld-flag.ts
 *
 * Deprecation/Removal Steps:
 * 1. Add example deprecated comment above the flag you are removing.
 * 2. Right click key and select "Find All References" to find all usages.
 * 3. Remove the flag from the enum.
 *
 * @see Naming Conventions
 * https://dutchie.atlassian.net/wiki/spaces/EN/pages/19934543881/LaunchDarkly+Flag+Naming+Convention
 * `{charter}.{module/team}.{name}.{rollout|experiment|entitlement|operational}`
 */
export enum LD {
  /**
   * @deprecated Fully enabled
   */
  A_THING_ROLLOUT_DEPRECATED_EXAMPLE = 'backoffice.platform.deprecated-example.rollout',
  DISCOUNTS_LOYALTY_POINT_BOOST = 'pos.backoffice.customers.discounts.loyalty-point-boost-fix.rollout',
  FINTECH_RETAIL_PAYMENTS_USE_GOODCANNA_INTEGRATION_ROLLOUT = 'fintech.retail-payments.good-canna-integration-enabled.rollout',
  INSIGHTS_RICH_EARLY_RELEASE_1 = 'pos.register.insights-rich-early-release-1',
  LOYALTY_SIGNUP_FIELD_CONFIG = 'pos.customers.loyalty-signup-field-config.rollout',
  MOBILE_CHECKOUT_SHOW_DRIVERS_LICENSE = 'pos.mobile-checkout.show-drivers-license',
  PRIMARY_IDENTIFICATION_BY_CUSTOMER_TYPE = 'pos.register.primary-identification-by-customer-type.rollout',
  IDENTIFICATION_PHOTO_PRESERVATION_V2 = 'pos.register.identification-photo-preservation-v2.rollout',
  IDENTIFICATION_PHOTO_PRESERVATION_V3 = 'pos.register.identification-photo-preservation-v3.rollout',
  NEW_LOYALTY_DISCOUNT_REDEMPTION_TYPE = 'core.cats-pos.new-loyalty-discount-type-ecomm-redemption',
  PENDO_API_KEY = 'pos.platform.pendo-api-key.operational',
  SCAN_TO_REDEEM_ROLLOUT = 'pos.backoffice.scan-to-redeem.rollout',
  REDEEM_LOYALTY_NEW_CTA = 'pos.register.customers.use-new-redeem-cta.rollout',
  /**
   * Search input on the "Redeem Loyalty Points" popup, for accounts with large
   * numbers of loyalty discounts (PF-8239 / POS-23129).
   */
  LOYALTY_REDEMPTION_SEARCH_ROLLOUT = 'pos.register.loyalty-redemption-search.rollout',
  POS_AUTO_EMAIL_RECEIPTS = 'pos.register.auto-email-receipts.rollout',
  PER_CATEGORY_RETURN_REASONS = 'pos.per-category-return-reasons.rollout',
  POS_CLEAR_FEES_ON_PAYMENT_TYPE_BACK = 'pos.register.clear-fees-on-payment-type-back.rollout',
  POS_REGISTER_AUTHORITATIVE_CHANGE_DUE_ROLLOUT = 'pos.register.authoritative-change-due.rollout',
  POS_CUSTOMERS_SUFFIX_SEARCH_FIX_ROLLOUT = 'pos.customers.customer-suffix-search-fix.rollout',
  /**
   * Nationwide check-in is the ability to search for and check in customers from other LSPs in the same org.
   */
  POS_ENABLE_NATIONWIDE_CHECKIN = 'pos.enable-nationwid-checkin', // yes there is a typo in the LD flag name
  POS_FETCH_BANK_ACCOUNT_INFO_ROLLOUT = 'fintech.retail-payments.fetch-bank-account-info.rollout',
  POS_REGISTER_DUTCHIE_INTELLIGENCE = 'pos.register.dutchie-intelligence.rollout',
  /**
   * Hides the Fees and donations cart action until the transaction has a valid shipment ID.
   * Off preserves the legacy behavior while the fix is rolled out.
   */
  POS_REGISTER_FEE_DONATION_VALID_SHIPMENT = 'pos.register.fee-donation-valid-shipment.rollout',
  POS_CUSTOMERS_LOCK_DOWN_SMS_TOGGLE = 'pos.customers.lock-down-sms-toggle.rollout',
  POS_CUSTOMERS_SHOW_CUSTOMER_AUDIT_HISTORY = 'pos.customers.show-customer-audit-history.rollout',
  POS_REGISTER_COPILOT_SCAN_TO_ADD = 'pos.register.copilot-scan-to-add.rollout',
  POS_REGISTER_INSIGHTS_RICH_CART = 'pos.register.insights-rich-cart.rollout',
  POS_REGISTER_SALES_ASSOCIATE_SCRIPTS = 'pos.register.sales-associate-scripts.rollout',
  POS_REGISTER_TRANSACTION_HISTORY_ECOMMERCE_ORDER_NUMBER = 'pos.register.transaction-history-ecommerce-order-number.rollout',
  POS_REGISTER_TRANSACTION_NOTES_FROM_CART = 'pos.register.transaction-notes-from-cart.rollout',
  POS_REGISTER_GAMIFICATION = 'core.cats-pos.gamification.register-cart.rollout',
  POS_REGISTER_LAB_RESULTS_ROLLOUT = 'pos.register.lab-results.rollout',
  POS_REGISTER_PRODUCT_SEARCH_TERPENE_COLUMN = 'pos.register.product-search-terpene-column.rollout',
  POS_REGISTER_PROFILE_COMPLETENESS = 'pos.register.customers.profile-completeness.rollout',
  POS_REGISTER_PROFILE_COMPLETENESS_LOYALTY_SETTING = 'pos.register.customers.profile-completeness-loyalty-setting.rollout',
  POS_REGISTER_SHOW_CUSTOMER_SEGMENTS_ON_PROFILE = 'pos.register.customers.show-segments-on-profile.rollout',
  POS_REGISTER_VERSIONS_MODAL = 'pos.register.versions-modal.rollout',
  FINTECH_MOBILE_CHECKOUT_PBB = 'fintech.retail-payments.mobile-checkout-pbb',
  FINTECH_MOBILE_CHECKOUT_PBB_REFRESH_CART = 'fintech.retail-payments.mobile-checkout-pbb-refresh-cart.rollout',
  FINTECH_MOBILE_CHECKOUT_ERROR_MESSAGING_UPDATES = 'fintech.retail-payments.mobile-checkout-error-messaging-updates.rollout',
  POS_SCAN_TO_PAY_IS_ENABLED = 'fintech.retail-payments.scan-to-pay.is-enabled',
  POS_SCAN_TO_PAY_ROLLOUT = 'fintech.retail-payments.scan-to-pay.rollout',
  POS_STARLINE_SUPPORT = 'pos.register.starline-printer-type.rollout',
  ID_SCAN_UNIFICATION = 'pos.register.id-scan-unification.rollout',
  /**
   * Gates the camera ID scan (POS-21494), AND-ed per surface with the location feature
   * `CameraIdScan` or `CameraIdScanMobileCheckout`. The key keeps the feature's old name because
   * leaflogix reads the same string.
   */
  POS_CAMERA_ID_SCAN_ROLLOUT = 'pos.register.id-ocr-capture.rollout',
  /**
   * With ID scan unification on, offers loyalty registration on every ID scan entry (POS-22325) from
   * the location's loyalty settings alone: an automatic check-in from any scan source, a manual
   * check-in, and a profile the scan created without checking in (offered right after creation, with
   * the Edit customer redirect following the modal). Inert with unification off. Off: no prompt after
   * an automatic check-in, and the manual check-in keeps its original gate. Kill-switch/rollout flag;
   * defaults to false.
   */
  POS_REGISTER_ID_SCAN_LOYALTY_PROMPT = 'pos.register.id-scan-loyalty-prompt.rollout',
  POS_CUSTOMERS_MMIC_MMJID_VALIDATION = 'pos.customers.mmic-mmjid-validation.rollout',
  POS_REGISTER_BACK_OFFICE_SESSION_HANDOFF = 'pos.register.back-office-session-handoff.rollout',
  /**
   * Hardware (PrintNode) label printing: render each label in parallel but hand them to the printer in cart
   * order (POS-23735). Off keeps the legacy behavior where labels reach the printer in network-completion order.
   * Rollout flag; defaults to false.
   */
  POS_REGISTER_PRINT_LABELS_PARALLEL_ORDERED = 'pos.register.print-labels-parallel-ordered.rollout',
  POS_REGISTER_CLOSEOUT_BALANCE_WARNING = 'pos.register.closeout-balance-warning.rollout',
  /**
   * Gates the configurable Manager PIN requirement and audit logging for manual cash drawer opens.
   * Off preserves the legacy always-require-PIN flow with no audit request.
   */
  POS_REGISTER_CASH_DRAWER_MANAGER_PIN = 'pos.register.cash-drawer-manager-pin.rollout',
  /**
   * When enabled, a failed barcode scan invalidates the product_SearchV2 React Query
   * cache so products recently moved to the sales floor become scannable on the next
   * attempt without waiting for the 10-minute stale time to expire. POS-19344.
   */
  POS_REGISTER_INVALIDATE_PRODUCT_CACHE_ON_SCAN_FAILURE = 'pos.register.invalidate-product-cache-on-scan-failure.rollout',
  /**
   * Gates the "Refresh discounts" cart overflow-menu action (POS-20051), which recomputes and
   * re-applies automatic discounts for the current cart. Same flag key gates the backend
   * refresh-discounts endpoint in leaflogix.
   */
  POS_REGISTER_REFRESH_DISCOUNTS_BUTTON = 'pos.register.discounts.refresh-discounts-button.rollout',
  POS_REGISTER_GUARD_TRANSIENT_CART_DETAILS = 'pos.register.guard-transient-cart-details.rollout',
  /**
   * Gates triggering a discount refresh (the same call as POS_REGISTER_REFRESH_DISCOUNTS_BUTTON)
   * immediately after a customer successfully signs up for Dutchie Loyalty from the cart, so a
   * newly-eligible loyalty-member-only discount is applied without a manual refresh.
   */
  DISCOUNT_LOYALTY_MEMBER_ONLY_REFRESH_ON_SIGNUP = 'core.cats-pos.discount-loyalty-member-only-refresh-on-signup.rollout',
  /**
   * Same flag key/semantics as leaflogix's LaunchDarklyFlag.AlpineIQPreCheckoutDisallowReusable:
   * when on, the BE blocks checkout outright for any AlpineIQ reward configured as non-reusable
   * or with a redemption cooldown, since Dutchie can't honor either restriction. The Register
   * mirrors this by disabling the Add button (and showing the incompatibility warning) up front,
   * so the cashier never gets to a checkout that the BE would block anyway.
   */
  ALPINE_IQ_PRE_CHECKOUT_DISALLOW_REUSABLE = 'core.cats.alpineiq-pre-checkout-disallow-reusable.rollout',
  /**
   * Same flag key/semantics as leaflogix's LaunchDarklyFlag.AlpineIQPreCheckoutValidateReusable:
   * when on (and ALPINE_IQ_PRE_CHECKOUT_DISALLOW_REUSABLE is off), the BE only blocks checkout
   * when a non-reusable/cooldown reward is applied more than once on the same cart - a single
   * use is fine. The Register mirrors this by allowing the reward to be added once, then
   * disabling it (with a single-use warning) once it's already applied to the current cart,
   * instead of disabling it outright.
   */
  ALPINE_IQ_PRE_CHECKOUT_VALIDATE_REUSABLE = 'core.cats.alpineiq-pre-checkout-validate-reusable.rollout',
  /**
   * When enabled, barcode scan lookups match product serial numbers case-sensitively
   * instead of the default case-insensitive comparison.
   */
  POS_REGISTER_CASE_SENSITIVE_BARCODE_SCAN = 'pos.register.case-sensitive-barcode-scan.rollout',
  /**
   * Gates the Register Closeout refresh guard (POS-14412): disables Close Out while
   * closeout details are refetching, and re-syncs New Balance to the refreshed starting
   * balance until the cashier edits it. Kill-switch: disable to revert to the prior
   * behavior if a stuck refetch ever blocks Close Out.
   */
  CLOSEOUT_REFRESH_GUARD_FIX = 'pos.register.closeout-refresh-guard-fix.rollout',
  /**
   * Gates the Register pre-closeout "Print check count" action: prints the Z Report summary for the
   * still-open window (since this register's last closeout) so staff have a check-count slip while
   * counting a cash drawer, before the actual closeout. Read-only - it settles nothing, mutates no
   * closeout state, and creates no financial records. Kill-switch: disable to hide the button (the
   * server routes simply go unused).
   */
  PRE_CLOSEOUT_Z_REPORT = 'pos.register.pre-closeout-z-report.rollout',
  /**
   * Gates the Close Out stale-totals fix (POS-20520): invalidates the `closeout-details` query
   * for the affected register when a transaction's payment type changes, so Cash income /
   * Ending balance / Current Balance refetch immediately instead of staying stale until an
   * unrelated action or a manual page reload. Kill-switch: disable to revert to no invalidation.
   */
  CLOSEOUT_REFETCH_ON_PAYMENT_TYPE_CHANGE = 'pos.register.closeout-refetch.rollout',
  /**
   * Gates the Close Out drawer overlay fix (POS-20512): switches the `BottomDrawer` backdrop
   * from `position: absolute` (effectively zero height, lets clicks reach elements behind an
   * open drawer) to `position: fixed` covering the full viewport, matching `PopupOverlay`.
   * Also adds a `smallTablet` wrap breakpoint to the Register Transactions `Row` layout.
   * Kill-switch: disable to revert to the prior (buggy) overlay behavior.
   */
  CLOSEOUT_DRAWER_OVERLAY_FIX = 'pos.register.close-out-drawer-overlay-fix.rollout',
  /**
   * Gates the open-transaction "User Activity" cart overflow-menu option (view activity log for
   * a sale that hasn't closed yet) and the redesigned User Activity modal (User/Activity filters
   * + sortable columns) used by both the open-cart and closed-transaction entry points.
   * Kill-switch: disable to revert to the prior modal (no filters/sorting, closed transactions only).
   */
  /**
   * Gates the in-flight/disabled guard on the preorder "Redeem" reward button
   * (PreorderRedemption.tsx). Prevents a double-tap or slow-network retry from firing two
   * concurrent apply-reward requests for the same reward, which is one contributing factor to a
   * race condition that can create duplicate LoyaltyDiscountRedemption/AppliedDiscount rows for
   * loyalty-vendor (e.g. AlpineIQ) redemptions on preorders.
   * Kill-switch: disable to revert to the prior (unguarded) button behavior.
   */
  POS_REGISTER_PREORDER_REDEEM_BUTTON_GUARD_FIX = 'core.cats-pos.discounts.preorder-redeem-button-guard-fix.rollout',
  /**
   * Gates the "Sync from OnFleet" row action on the Register Delivery > Routes tab (POS-18849,
   * epic POS-18583). Shows a read-only diff preview (sync-from-onfleet) then, on confirmation,
   * applies OnFleet's stop order/driver/vehicle locally (apply-onfleet-sync) -- OnFleet wins on
   * conflict. This is a UI rollout flag, separate from the backend's
   * `pos.delivery.onfleet-bidirectional-sync.rollout` killswitch -- the backend enforces its own
   * flag and returns a "not enabled" message that the UI surfaces as a toast, so this flag can
   * roll out independently (e.g. after the backend flag is already fully on).
   */
  /**
   * Gates enforcement of Back-Office-configured required medical fields (e.g. MMJ ID) when a
   * customer is switched to Medical via either in-cart "Change Customer Type" modal, when
   * checking out as an already-Medical customer, and when saving the Create/Edit Customer form
   * for a customer who is (or defaults to) Medical without the Customer Type dropdown being
   * touched. Kill-switch: disable to revert to the prior (unguarded) behavior if the guard
   * incorrectly blocks legitimate customer-type changes or checkouts.
   */
  POS_MMJ_CHANGE_REQUIREMENT_FIX = 'pos.mmj-change-requirement-fix.rollout',
  /**
   * NOTE: misleading name - this flag gates ONLY Check, not Manual. See FINTECH_MOBILE_CHECKOUT_MANUAL.
   */
  FINTECH_MOBILE_CHECKOUT_CHECK_AND_MANUAL = 'fintech.retail-payments.check-and-manual-on-mc.rollout',
  /**
   * Gates Manual (non-integrated processor) payment support on mobile-checkout - previously only
   * available on register. Mirrors register's existing EnableManualPayments location feature,
   * which still independently controls whether Manual is actually offered once this flag is on.
   * Kill-switch: disable to hide Manual and revert to the prior mobile-checkout payment method set
   * (Check, gated by the separate, misleadingly-named FINTECH_MOBILE_CHECKOUT_CHECK_AND_MANUAL
   * flag, is unaffected either way).
   */
  FINTECH_MOBILE_CHECKOUT_MANUAL = 'fintech.retail-payments.allow-manual-payments-on-mc.rollout',
  POS_REGISTER_BLOCK_CART_REMOVAL_DURING_PAYMENT = 'pos.register.block-cart-removal-during-payment.rollout',
  POS_TRACEABILITY_ALLOTMENT_ITEM_BREAKDOWN = 'pos.traceability.allotment-item-breakdown.rollout',
  POS_TRACEABILITY_METRC_WARNINGS_SURFACED = 'pos.traceability.metrc-warnings-surfaced.rollout',
  /**
   * Gates the "Received" (order placed) column on the Register Delivery > Routes table
   * (mirrors the existing "Received" field already shown on the Guest List order card, sourced
   * from the same underlying ShipmentDateUTC). Requires the paired leaflogix backend change that
   * adds ShipmentDateUTC to POS3.GetDeliveryOrdersV2 -- flag defaults to false/off so this can
   * ship ahead of that backend rollout without the column silently rendering blank timestamps.
   * Kill-switch: disable to hide the column and revert to the prior Delivery Routes table layout.
   */
  POS_DELIVERY_RECEIVED_COLUMN = 'pos.register.delivery.received-column.rollout',
  /**
   * Gates differentiated error messaging for transaction voids (POS-20322).
   * When enabled, distinguishes manager PIN lookup failures from general backend void
   * errors, showing the specific backend error message or neutral support fallback instead
   * of blaming the PIN for server-side failures.
   * Kill-switch: disable to revert to legacy behavior (blaming the PIN on all non-restriction errors).
   */
  POS_REGISTER_VOID_TRANSACTION_ERROR_MESSAGING = 'pos.register.void-transaction-error-messaging.rollout',
  /**
   * Rollout for the pre-order fulfillment checkout hard-gate (POS-22713). While off, checkout
   * always falls back to the legacy dismissible warning toast, regardless of the
   * RequirePreOrderFulfillmentAtCheckout location setting - use this as the kill-switch for the
   * whole hard-gate capability during rollout. Once on, the location setting decides hard-gate
   * (blocking modal) vs soft-warning (toast) per location.
   */
  POS_REGISTER_PREORDER_FULFILLMENT_HARD_GATE = 'pos.register.preorder-fulfillment-hard-gate.rollout',
  /** Imgix domain for AWS S3-hosted images. Empty = disabled. Shared with back-office. */
  CATALOG_IMGIX_AWS_DOMAIN = 'pos.backoffice.catalog-imgix-aws-domain.operational',
  /** Imgix domain for Azure-hosted images. Empty = disabled. Shared with back-office. */
  CATALOG_IMGIX_AZURE_DOMAIN = 'pos.backoffice.catalog-imgix-azure-domain.operational',
}
